import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  FormControlLabel,
  InputBase,
  Divider,
  TablePagination,
  Drawer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from "@mui/icons-material/Close";
import ChecksGrid from "./ChecksGrid";

const StyledChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === "FAILED"
    ? "#ffebee"
    : status === "MANUAL"
      ? "#e3f2fd"
      : "#e8f5e9",
  color: status === "FAILED"
    ? "#d32f2f"
    : status === "MANUAL"
      ? "#1976d2"
      : "#2e7d32",
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 24,
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

// Helper to get column mapping based on compliance
const getColumnMapping = (compliance) => {
  switch (compliance) {
    case 'NIS2':
      return { controlId: 'ControlId', title: 'Attributes[0].SubSection' };
    case 'CIS-2.0':
    case 'CIS-2.1':
    case 'CIS-3.0':
    case 'CIS-4.0.1':
    case 'CIS-5.0':
    case 'CIS-6.0':
    case 'CIS-7.0':
      return { controlId: 'ControlId', title: 'Description' };
    case 'CISA':
      return { controlId: 'ControlId', title: 'Name' };
    case 'ENS RD2022':
      return { controlId: 'ControlId', title: 'Description' };
    case 'FFIEC':
      return { controlId: 'Name', title: 'Name' };
    case 'ISO27001-2013':
      return { controlId: 'Attributes[0].Objective_ID', title: 'Attributes[0].Objective_Name' };
    case 'KISA-ISMS-P-2023':
      return { controlId: 'ControlId', title: 'Attributes[0].Section' };
    case 'NIST-800-171-Revision-2':
      return { controlId: 'Attributes[0].ItemId', title: 'Name' };
    case 'SOC2':
      return { controlId: 'Attributes[0].ItemId', title: 'Name' };
    case 'PCI-DSS-4.0':
    case 'PCI-4.0':
      return { controlId: 'ControlId', title: 'ControlId' };
    case 'FedRAMP-Low-Revision-4':
    case 'FedRAMP-20x-KSI-Low-25.05C':
      return { controlId: 'ControlId', title: 'Name' };
    case 'GDPR':
      return { controlId: 'Attributes[0].ItemID', title: 'Attributes[0].Section' };
    case 'FedRamp-Moderate-Revision-4':
      return { controlId: 'ControlId', title: 'Name' };
    case 'GxP-EU-Annex-11':
      return { controlId: 'Attributes[0].ItemID', title: 'Name' };
    case 'GxP-21-CFR-Part-11':
      return { controlId: 'Name', title: 'Name' };
    case 'HIPAA':
      return { controlId: 'Attributes[0].ItemID', title: 'Name' };
    case 'ISO27001-2022':
      return { controlId: 'Attributes[0].Objetive_ID', title: 'Attributes[0].Objetive_Name' };
    case 'NIST-800-53-Revision-5':
      return { controlId: 'ControlId', title: 'Attributes[0].SubSection' };
    case 'RBI-Cyber-Security-Framework':
      return { controlId: 'ControlId', title: 'Name' };
    case 'NIST-CSF-1.1':
      return { controlId: 'ControlId', title: 'Attributes[0].SubSection' };
    case 'MITRE-ATTACK':
      return { controlId: 'ControlId', title: 'Description' };
    case 'SecNumCloud-3.2':
      return { controlId: 'ControlId', title: 'Description' };
    case 'CSA-CCM-4.0':
      return { controlId: 'ControlId', title: 'Description' };
    case 'NIS2':
      return { controlId: 'ControlId', title: 'Description' };
    case 'C5-2025':
      return { controlId: 'ControlId', title: 'Description' };
    case 'CCC-v2025.10':
      return { controlId: 'ControlId', title: 'Description' };
    default:
      return { controlId: 'ControlId', title: 'Description' };
  }
};

// Helper to get expandable fields based on compliance
const getExpandableFields = (compliance) => {
  switch (compliance) {
    case 'CIS-4.0.1':
    case 'CIS-5.0':
    case 'CIS-6.0':
    case 'CIS-7.0':
      return [
        { key: 'Description', path: 'Attributes[0].Description' },
        { key: 'Rationale Statement', path: 'Attributes[0].RationaleStatement' },
        // { key: 'Remediation', path: 'Attributes[0].RemediationProcedure' },
        { key: 'Remediation Reference', path: 'Attributes[0].References' }
      ];
    case 'CISA':
      return [
        { key: 'Description', path: 'Description' }
      ];
    case 'ENS RD2022':
      return [
        { key: 'Description', path: 'Attributes[0].DescripcionControl' },
        { key: 'Nivel', path: 'Attributes[0].Nivel' },
        { key: 'Tipo', path: 'Attributes[0].Tipo' },
        { key: 'Dimensiones', path: 'Attributes[0].Dimensiones' }
      ];
    case 'FedRAMP-Low-Revision-4':
      return [
        { key: 'Description', path: 'Description' }
      ];
    case 'FedRamp-Moderate-Revision-4':
      return [
        { key: 'Description', path: 'Description' },
        { key: 'Subsection', path: 'Attributes[0].Subsection' }
      ];
    case 'FFIEC':
      return [
        { key: 'Description', path: 'Attributes[0].Description' }
      ];
    case 'GDPR':
      return [
        { key: 'Description', path: 'Description' }
      ];
    case 'GxP-21-CFR-Part-11':
      return [
        { key: 'Description', path: 'Description' },
        { key: 'Subpart C', path: '"Electronic Signatures"' },
        { key: 'Section', path: 'Attributes[0].Section' },
        { key: 'Control ID', path: '11.200, 11.300(b), 11.330(d)' },
        { key: 'Control Name', path: 'Name' },
        { key: 'Description', path: 'Description' }
      ];
    case 'GxP-EU-Annex-11':
      return [
        { key: 'Description', path: 'Description' }
      ];
    case 'ISO27001-2013':
    case 'ISO27001-2022':
      return [
        { key: 'Description', path: 'Attributes[0].Check_Summary' }
      ];
    default:
      return [
        { key: 'Description', path: 'Description' }
      ];
  }
};

// Add Title column
const DEFAULT_COLUMNS = [
  { key: "ControlId", label: "Control ID", minWidth: 120 },
  { key: "Title", label: "Title", minWidth: 180 },
  { key: "Status", label: "Status", minWidth: 100 },
  { key: "TotalChecks", label: "Total Checks", minWidth: 120 },
  { key: "UniqueResourcesScanned", label: "Resources Scanned", minWidth: 140 },
];

// Severity color mapping (same as ChecksGrid)
const SEVERITY_COLORS = {
  CRITICAL: "#b71c1c",
  HIGH: "#f44336",
  MEDIUM: "#fbc02d",
  LOW: "#4caf50",
  INFORMATIONAL: "#1976d2",
  DEFAULT: "#bdbdbd",
};

const ControlsGrid = ({ controls = [], onResourceClick, compliance, checks = [] }) => {
  const controlsArray = useMemo(
    () => (Array.isArray(controls) ? controls : Object.values(controls)),
    [controls]
  );
  const [activeFilters, setActiveFilters] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({}); // For CIS-5.0 grouping
  const [search, setSearch] = useState("");
  const [checksDrawerOpen, setChecksDrawerOpen] = useState(false);
  const [selectedControlChecks, setSelectedControlChecks] = useState([]);
  const [selectedControlId, setSelectedControlId] = useState(null);

  // Helper function to get nested values from object
  const getNestedValue = (obj, path) => {
    if (!path) return '';
    if (!path.includes('.')) return obj[path];
    const parts = path.split('.');
    let value = obj;
    for (const part of parts) {
      if (part.includes('[')) {
        const [arrayName, index] = part.split('[');
        const arrayIndex = parseInt(index);
        value = value[arrayName]?.[arrayIndex];
      } else {
        value = value?.[part];
      }
      if (value === undefined) return '';
    }
    return value;
  };

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtering logic
  const calculateMetrics = (controlsArr) => {
    if (!controlsArr || !Array.isArray(controlsArr)) {
      return {
        total: 0,
        passed: 0,
        failed: 0,
        manual: 0,
      };
    }
    return {
      total: controlsArr.length,
      passed: controlsArr.filter(control => control.Status === "PASSED").length,
      failed: controlsArr.filter(control => control.Status === "FAILED").length,
      manual: controlsArr.filter(control => control.Status === "MANUAL").length,
    };
  };
  const metrics = calculateMetrics(controlsArray);

  const handleFilterChange = (filterType) => {
    if (filterType === "total") return;
    let newFilters = [...activeFilters];
    if (activeFilters.includes(filterType)) {
      newFilters = newFilters.filter(f => f !== filterType);
    } else {
      newFilters.push(filterType);
    }
    setActiveFilters(newFilters);
  };

  // FilterChip component
  const FilterChip = ({ label, count, type, color = "default", variant = "outlined", disabled = false }) => {
    const isActive = activeFilters.includes(type);
    const getChipColors = () => {
      switch (type) {
        case "passed":
          return { bg: isActive ? "#c8e6c9" : "#e8f5e9", color: "#2e7d32", border: isActive ? "2px solid #2e7d32" : "none" };
        case "failed":
          return { bg: isActive ? "#ffcdd2" : "#ffebee", color: "#d32f2f", border: isActive ? "2px solid #d32f2f" : "none" };
        case "manual":
          return { bg: isActive ? "#bbdefb" : "#e3f2fd", color: "#1976d2", border: isActive ? "2px solid #1976d2" : "none" };
        default:
          return { bg: isActive ? "rgba(0, 0, 0, 0.08)" : "rgba(0, 0, 0, 0.04)", color: "text.secondary", border: "none" };
      }
    };
    const colors = getChipColors();
    return (
      <Chip
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption">{label}:</Typography>
            <Typography variant="caption" fontWeight={600}>{count}</Typography>
            {!disabled && (isActive ? <RemoveIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <AddIcon sx={{ fontSize: 16, ml: 0.5 }} />)}
          </Box>
        }
        onClick={() => !disabled && handleFilterChange(type)}
        size="small"
        color={color}
        variant={isActive ? "filled" : variant}
        sx={{
          height: "24px",
          backgroundColor: colors.bg,
          color: colors.color,
          border: colors.border,
          "& .MuiChip-label": { color: colors.color },
          "&:hover": { backgroundColor: disabled ? colors.bg : isActive ? colors.bg : colors.bg, opacity: disabled ? 1 : 0.8 },
          cursor: disabled ? "default" : "pointer",
          transition: "all 0.2s ease-in-out",
        }}
      />
    );
  };

  // Get mapping for columns based on compliance
  const columnMapping = getColumnMapping(compliance);

  // Helper function to extract group prefix (e.g., "2.1" from "2.1.1")
  const getGroupPrefix = (controlId) => {
    if (!controlId) return null;
    const parts = String(controlId).split('.');
    if (parts.length >= 2) {
      return `${parts[0]}.${parts[1]}`;
    }
    return null;
  };

  // Helper function to check if control should be grouped (only 2.x except 2.1; not 5.1)
  const shouldGroupControl = (controlId) => {
    if (!controlId) return false;
    const idStr = String(controlId);
    const prefix = getGroupPrefix(idStr);
    // Do not create synthetic parent rows for 2.1 and 5.1
    if (prefix === "2.1" || prefix === "5.1") return false;
    // Group other Storage prefixes (e.g. 2.2, 2.3)
    return idStr.startsWith("2.");
  };

  // Group controls for CIS-5.0 by prefix (e.g., 2.1, 2.2, 5.1)
  const groupControlsForCIS50 = (controls) => {
    const groups = new Map();
    const other = [];

    controls.forEach(control => {
      if (shouldGroupControl(control.ControlId)) {
        const prefix = getGroupPrefix(control.ControlId);
        if (prefix) {
          if (!groups.has(prefix)) {
            groups.set(prefix, []);
          }
          groups.get(prefix).push(control);
        } else {
          other.push(control);
        }
      } else {
        other.push(control);
      }
    });

    // Convert Map to sorted array of [prefix, controls]
    const sortedGroups = Array.from(groups.entries()).sort((a, b) => {
      const aParts = a[0].split('.').map(Number);
      const bParts = b[0].split('.').map(Number);
      if (aParts[0] !== bParts[0]) return aParts[0] - bParts[0];
      return aParts[1] - bParts[1];
    });

    return { groups: sortedGroups, other };
  };

  // Mapping of known group titles from CIS-5.0
  // const GROUP_TITLE_MAP = {
  //   '2.1': 'Simple Storage Service (S3)',
  //   '2.2': 'Relational Database Service (RDS)',
  //   '2.3': 'Elastic File System (EFS)',
  //   '5.1': 'Elastic Compute Cloud (EC2)',
  // };

  // Get group title from the first control in the group
  const getGroupTitle = (prefix, controls) => {
    // First check if we have a known title mapping
    // if (GROUP_TITLE_MAP[prefix]) {
    //   return GROUP_TITLE_MAP[prefix];
    // }
    
    // Otherwise try to extract from control description
    if (!controls || controls.length === 0) return '';
    const firstControl = controls[0];
    const titleValue = getNestedValue(firstControl, columnMapping.title);
    
    if (titleValue && typeof titleValue === 'string') {
      // Look for pattern like "5.1 - Elastic Compute Cloud (EC2)" or "5.1 Elastic Compute Cloud (EC2)"
      // Try to extract the title part after the prefix
      const patterns = [
        new RegExp(`^${prefix.replace('.', '\\.')}\\s*-\\s*(.+)$`, 'i'), // "5.1 - Title"
        new RegExp(`^${prefix.replace('.', '\\.')}\\s+(.+)$`, 'i'), // "5.1 Title"
      ];
      
      for (const pattern of patterns) {
        const match = titleValue.match(pattern);
        if (match && match[1]) {
          return match[1].trim();
        }
      }
      
      // If no pattern match, try to find text after the prefix in the description
      const prefixIndex = titleValue.indexOf(prefix);
      if (prefixIndex !== -1) {
        const afterPrefix = titleValue.substring(prefixIndex + prefix.length).trim();
        // Remove leading dash or colon if present
        const cleaned = afterPrefix.replace(/^[-:\s]+/, '').trim();
        if (cleaned) {
          return cleaned;
        }
      }
    }
    return '';
  };

  // Toggle group expansion
  const toggleGroup = (groupPrefix) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupPrefix]: !prev[groupPrefix]
    }));
  };

  useEffect(() => {
    let rows = controlsArray;
    if (activeFilters.length > 0) {
      rows = rows.filter(control => activeFilters.includes(control.Status.toLowerCase()));
    }
    const SEARCHABLE_FIELDS = ['ControlId', 'Status', 'Section', 'UniqueResourcesScanned', 'Name', 'Description'];
    if (search) {
      rows = rows.filter(row =>
        SEARCHABLE_FIELDS.some(field =>
          row[field] && String(row[field]).toLowerCase().includes(search.toLowerCase())
        )
      );
    }
    setFilteredRows(rows);
  }, [activeFilters, controlsArray, search]);

  const handleResourceClick = (controlId, resourcesUri, row) => {
    if (typeof onResourceClick === 'function') {
      onResourceClick(controlId, resourcesUri, row);
    }
  };

  // Table columns to render
  const visibleColumns = DEFAULT_COLUMNS;

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated rows
  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // Helper function to get checks for a control
  // Checks can be:
  // 1. Nested in the control object (control.Checks)
  // 2. In a separate checks array passed as prop, matched by ControlId
  // 3. In a flat checks array where all checks belong to all controls
  const getChecksForControl = (controlId, controlObj = null) => {
    // First, check if checks are nested in the control object
    if (controlObj && controlObj.Checks && Array.isArray(controlObj.Checks)) {
      return controlObj.Checks;
    }
    
    // Second, try to match from the checks prop array
    if (checks && Array.isArray(checks)) {
      // If checks have a ControlId field, match by it
      const matchedChecks = checks.filter(check => {
        if (check.ControlId) {
          return check.ControlId === controlId;
        }
        return false;
      });
      
      if (matchedChecks.length > 0) {
        return matchedChecks;
      }
      
      // If no ControlId match found and checks array exists but no ControlId field,
      // return all checks (assuming they all belong to this control)
      // This is a fallback - adjust based on actual API structure
      if (checks.length > 0 && !checks[0].ControlId) {
        return checks;
      }
    }
    
    return [];
  };

  // Helper to get total checks count for a control
  const getTotalChecksForControl = (controlId, controlObj = null) => {
    return getChecksForControl(controlId, controlObj).length;
  };

  // Render a control row
  const renderControlRow = (row, idx, isChild = false) => {
    return (
      <React.Fragment key={row.ControlId || idx}>
        <TableRow hover selected={expandedRow === (row.ControlId || idx)} sx={{ backgroundColor: isChild ? '#fafafa' : 'inherit' }}>
          {visibleColumns.map(col => {
            if (col.key === "ControlId") {
              return (
                <TableCell key={col.key} align="center" sx={{ pl: isChild ? 4 : 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        setExpandedRow(expandedRow === (row.ControlId || idx) ? null : (row.ControlId || idx));
                      }}
                      aria-label={expandedRow === (row.ControlId || idx) ? 'Collapse row' : 'Expand row'}
                    >
                      {expandedRow === (row.ControlId || idx) ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="body2" fontWeight={500}>{row.ControlId}</Typography>
                  </Box>
                </TableCell>
              );
            }
            if (col.key === "Title") {
              const titleValue = getNestedValue(row, columnMapping.title);
              return (
                <TableCell key={col.key} align="left">
                  <Typography variant="body2" fontWeight={500}>{titleValue}</Typography>
                </TableCell>
              );
            }
            if (col.key === "Status") {
              return (
                <TableCell key={col.key} align="center">
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      color:
                        row.Status === "FAILED"
                          ? "#d32f2f"
                          : row.Status === "MANUAL"
                          ? "#1976d2"
                          : row.Status === "PASSED"
                          ? "#2e7d32"
                          : "#888",
                      fontSize: 15,
                    }}
                  >
                    {row.Status}
                  </Typography>
                </TableCell>
              );
            }
            if (col.key === "Section") {
              return (
                <TableCell key={col.key} align="center">{row.Section}</TableCell>
              );
            }
            if (col.key === "UniqueResourcesScanned") {
              const resourceCount = row.UniqueResourcesScanned || 0;
              const isDisabled = resourceCount === 0;
              
              return (
                <TableCell key={col.key} align="center">
                  <Tooltip title={isDisabled ? "No Resources found for this control" : `${resourceCount} unique resources were scanned`}>
                    <Chip
                      label={resourceCount}
                      size="small"
                      onClick={isDisabled ? undefined : () => handleResourceClick(row.ControlId, row.ResourcesUri, row)}
                      sx={{
                        backgroundColor: isDisabled ? "#f5f5f5" : "#e3f2fd",
                        color: isDisabled ? "#9e9e9e" : "#1976d2",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        filter: isDisabled ? "blur(0.5px)" : "none",
                        opacity: isDisabled ? 0.6 : 1,
                        "&:hover": { 
                          backgroundColor: isDisabled ? "#f5f5f5" : "#bbdefb" 
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    />
                  </Tooltip>
                </TableCell>
              );
            }
            if (col.key === "TotalChecks") {
              const checksCount = getTotalChecksForControl(row.ControlId, row);
              const isDisabled = checksCount === 0;
              const controlChecks = getChecksForControl(row.ControlId, row);
              
              return (
                <TableCell key={col.key} align="center">
                  <Tooltip title={isDisabled ? "No checks found for this control" : `Click to view ${checksCount} ${checksCount === 1 ? 'check' : 'checks'}`}>
                    <Chip
                      label={checksCount}
                      size="small"
                      onClick={isDisabled ? undefined : () => {
                        setSelectedControlChecks(controlChecks);
                        setSelectedControlId(row.ControlId);
                        setChecksDrawerOpen(true);
                      }}
                      sx={{
                        backgroundColor: isDisabled ? "#f5f5f5" : "#e3f2fd",
                        color: isDisabled ? "#9e9e9e" : "#1976d2",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: isDisabled ? "#f5f5f5" : "#bbdefb",
                        },
                      }}
                    />
                  </Tooltip>
                </TableCell>
              );
            }
            return null;
          })}
        </TableRow>
        {/* Expandable detail row */}
        {expandedRow === (row.ControlId || idx) && (
          <TableRow>
            <TableCell
              colSpan={visibleColumns.length}
              sx={{
                background: '#f8fafd',
                borderBottom: '1px solid #e3eafc',
                px: 2,
                py: 1.5,
                verticalAlign: 'top',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {getExpandableFields(compliance)
                  .map((field) => {
                    const rawValue = getNestedValue(row, field.path);
                    if (rawValue === undefined || rawValue === null || rawValue === '') return null;
                    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
                    if (!value || (typeof value === 'string' && !value.length)) return null;
                    return { field, value };
                  })
                  .filter(Boolean)
                  .map(({ field, value }, index) => (
                    <Box key={`${field.key}-${index}`}>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ color: '#444', fontSize: 14, lineHeight: 1.5 }}
                      >
                        <Box component="span" sx={{ fontWeight: 600, mr: 0.75 }}>
                          {field.key}:
                        </Box>
                        {field.key === 'Remediation Reference' ? (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: 0.5,
                              verticalAlign: 'top',
                            }}
                          >
                            {(() => {
                              const links = String(value)
                                .split(/(?=https?:\/\/)/g)
                                .map((url) => url.trim())
                                .filter(Boolean);
                              if (links.length > 0 && links.some((url) => /^https?:\/\//i.test(url))) {
                                return links.map((url, i) => {
                                  let cleanedUrl = url.replace(/[:;]\s*(?=https?:\/\/)/, '').trim();
                                  cleanedUrl = cleanedUrl.replace(/[:;]+$/, '');
                                  if (!cleanedUrl) return null;
                                  return (
                                    <a
                                      key={i}
                                      href={cleanedUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: '#1976d2',
                                        textDecoration: 'underline',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      {cleanedUrl}
                                    </a>
                                  );
                                });
                              }
                              return String(value);
                            })()}
                          </Box>
                        ) : (
                          <Box component="span" sx={{ color: '#555' }}>
                            {String(value)}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      overflow: "hidden", 
      display: 'flex',
      flexDirection: 'column',
      height: '100%'  // Changed from calc(100vh - 280px)
    }}>
      {/* Toolbar */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterChip label="Total" count={metrics.total} type="total" variant="filled" disabled={true} />
            <FilterChip label="Passed" count={metrics.passed} type="passed" variant="filled" />
            <FilterChip label="Failed" count={metrics.failed} type="failed" variant="filled" />
            <FilterChip label="Manual" count={metrics.manual} type="manual" variant="filled" />
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', background: '#F1F1F1', borderRadius: 1, px: 1, height: 36, minWidth: 200 }}>
            <SearchIcon sx={{ color: '#888', fontSize: 20, mr: 1 }} />
            <InputBase
              placeholder="Search Controls"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ width: 200, fontSize: 14 }}
            />
          </Box>
        </Box>
      </Paper>
      {/* Table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2, 
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
          flex: 1,
          overflow: 'auto'
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map(col => (
                <TableCell 
                  key={col.key} 
                  align={col.key === "Title" || col.key === "ControlId" ? "left" : "center"} 
                  sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {compliance === 'CIS-5.0' ? (
              // Grouped view for CIS-5.0
              (() => {
                const { groups, other } = groupControlsForCIS50(paginatedRows);
                const rowsToRender = [];
                
                // Render each group (e.g., 2.1, 2.2, 5.1)
                groups.forEach(([prefix, controls]) => {
                  // Only show expand if group has children
                  const hasChildren = controls.length > 0;
                  const isExpanded = expandedGroups[prefix] !== undefined ? expandedGroups[prefix] : false; // Default to collapsed
                  const groupTitle = hasChildren ? getGroupTitle(prefix, controls) : '';
                  
                  // Calculate aggregated resources scanned (sum)
                  const totalResourcesScanned = controls.reduce((sum, control) => {
                    return sum + (control.UniqueResourcesScanned || 0);
                  }, 0);
                  
                  // Calculate aggregated status: FAILED if any failed, otherwise PASSED
                  const aggregatedStatus = controls.some(control => control.Status === 'FAILED') 
                    ? 'FAILED' 
                    : controls.some(control => control.Status === 'MANUAL')
                    ? 'MANUAL'
                    : 'PASSED';
                  
                  rowsToRender.push(
                    <TableRow 
                      key={`group-${prefix}`} 
                      sx={{ 
                        backgroundColor: '#f5f5f5', 
                        cursor: hasChildren ? 'pointer' : 'default',
                        '&:hover': { backgroundColor: hasChildren ? '#eeeeee' : '#f5f5f5' }
                      }}
                      onClick={hasChildren ? () => toggleGroup(prefix) : undefined}
                    >
                      {visibleColumns.map(col => {
                        if (col.key === "ControlId") {
                          return (
                            <TableCell key={col.key} align="center" sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {hasChildren && (
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleGroup(prefix); }}>
                                    {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                  </IconButton>
                                )}
                                <Typography variant="body1" fontWeight={600}>
                                  {prefix}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        }
                        if (col.key === "Title") {
                          return (
                            <TableCell key={col.key} align="left" sx={{ py: 1.5 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {groupTitle || ''}
                              </Typography>
                            </TableCell>
                          );
                        }
                        if (col.key === "Status") {
                          return (
                            <TableCell key={col.key} align="center" sx={{ py: 1.5 }}>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{
                                  color:
                                    aggregatedStatus === "FAILED"
                                      ? "#d32f2f"
                                      : aggregatedStatus === "MANUAL"
                                      ? "#1976d2"
                                      : aggregatedStatus === "PASSED"
                                      ? "#2e7d32"
                                      : "#888",
                                  fontSize: 15,
                                }}
                              >
                                {aggregatedStatus}
                              </Typography>
                            </TableCell>
                          );
                        }
                        if (col.key === "UniqueResourcesScanned") {
                          return (
                            <TableCell key={col.key} align="center" sx={{ py: 1.5 }}>
                              <Chip
                                label={totalResourcesScanned}
                                size="small"
                                sx={{
                                  backgroundColor: "#f5f5f5",
                                  color: "#9e9e9e",
                                  cursor: "default",
                                  opacity: 0.8,
                                }}
                              />
                            </TableCell>
                          );
                        }
                        if (col.key === "TotalChecks") {
                          // Calculate total checks for all controls in the group
                          // For group rows (2.1, 2.2, 2.3, 5.1), just show total, not clickable
                          const totalChecks = controls.reduce((sum, control) => {
                            return sum + getTotalChecksForControl(control.ControlId, control);
                          }, 0);
                          
                          return (
                            <TableCell key={col.key} align="center" sx={{ py: 1.5 }}>
                              <Tooltip title={`Total checks for this group: ${totalChecks}`}>
                                <Chip
                                  label={totalChecks}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#f5f5f5",
                                    color: "#9e9e9e",
                                    cursor: "default",
                                    opacity: 0.8,
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  );
                  
                  // Render controls in this group if expanded and has children
                  if (hasChildren && isExpanded) {
                    controls.forEach((control, idx) => {
                      rowsToRender.push(renderControlRow(control, `${prefix}-${idx}`, true));
                    });
                  }
                });
                
                // Render other controls (not Storage or Networking)
                if (other.length > 0) {
                  other.forEach((control, idx) => {
                    rowsToRender.push(renderControlRow(control, `other-${idx}`, false));
                  });
                }
                
                return rowsToRender.length > 0 ? rowsToRender : (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No controls found</Typography>
                    </TableCell>
                  </TableRow>
                );
              })()
            ) : (
              // Normal view for other compliance standards
              paginatedRows.length > 0 ? (
                paginatedRows.map((row, idx) => renderControlRow(row, idx, false))
              ) : (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No controls found</Typography>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: '1px solid #e0e0e0',
          '.MuiTablePagination-toolbar': {
            height: '52px',
          },
        }}
      />
      
      {/* Checks Drawer */}
      <Drawer 
        anchor="right" 
        open={checksDrawerOpen} 
        onClose={() => setChecksDrawerOpen(false)}
        PaperProps={{ 
          sx: { 
            width: { xs: '100%', sm: '90%', md: 800, lg: 1000, xl: 1200 }, 
            maxWidth: '100vw' 
          } 
        }}
      >
        <Box sx={{ p: 3, minHeight: '100vh', background: "#F9F9F9" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" color="#333">
              Checks for Control: <b>{selectedControlId}</b> | {selectedControlChecks.length} {selectedControlChecks.length === 1 ? 'check' : 'checks'}
            </Typography>
            <IconButton onClick={() => setChecksDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <ChecksGrid checks={selectedControlChecks} />
        </Box>
      </Drawer>
    </Box>
  );
};

export default ControlsGrid; 